var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useMemo } from "react";
import DOMPurify from 'dompurify';
import QuestionnaireOrg from "../../../organisms/jsQuestionnaire/jsQuestionnaire";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { KnowledgeAction, KnowledgeFormAction, } from "../../../../redux/actions/job_seeker_questionnaire";
import { questionnaireTimer } from "../../../../utils/QuestionnaireTimer";
import { getQuestionnaireAttemptVariant, getQuestionnaireProgressWidth, } from "../../../../utils/questionnaireProgress";
/**
 * The Knowledge Questionnaire Page
 * This will need to call all relevant APIs to get the values.
 * @param props
 * @returns
 */
var EXCLUSIVE_CHECKBOX_FIELDS = {
    jobseeker_jobsearchsource: ["None"],
};
function KnowledgeQuestionnaire() {
    var navigate = useNavigate();
    var dispatch = useDispatch();
    var _a = useState(''), error = _a[0], setError = _a[1];
    var _b = useState(false), isSubmitting = _b[0], setIsSubmitting = _b[1];
    var _c = useState({}), disabledCheckboxOptions = _c[0], setDisabledCheckboxOptions = _c[1];
    // using selector hook to fetch data from the reducer
    var _d = useSelector(function (state) { return state.rootReducer.JobSeekerQuestionnaireReducer; }), knowledeData = _d.knowledeData, error_knowledge = _d.error_knowledge, loading = _d.loading, knowledeFormData = _d.knowledeFormData, success_knowlede_form = _d.success_knowlede_form;
    var loginData = useSelector(function (state) { return state.rootReducer.userReducer; }).loginData;
    var quesAttemptDetails = useSelector(function (state) { return state.rootReducer.dashboardReducer.quesAttemptDetails; });
    var getFieldChoices = function (fieldKey) {
        var _a, _b;
        var fieldItem = knowledeData === null || knowledeData === void 0 ? void 0 : knowledeData.find(function (item) { return Object.keys(item)[0] === fieldKey; });
        return fieldItem ? (_b = (_a = fieldItem[fieldKey]) === null || _a === void 0 ? void 0 : _a.choices) !== null && _b !== void 0 ? _b : [] : [];
    };
    var normalizeExclusiveCheckboxSelection = function (fieldKey, selectedValues) {
        if (selectedValues === void 0) { selectedValues = []; }
        var exclusiveValues = EXCLUSIVE_CHECKBOX_FIELDS[fieldKey];
        if (!exclusiveValues) {
            return { values: selectedValues, disabled: new Set() };
        }
        var uniqueSelected = selectedValues.filter(function (val, index, arr) { return arr.indexOf(val) === index; });
        var selectedExclusive = uniqueSelected.filter(function (val) {
            return exclusiveValues.includes(val);
        });
        var selectedOther = uniqueSelected.filter(function (val) { return !exclusiveValues.includes(val); });
        if (selectedExclusive.length > 0) {
            var activeExclusive = selectedExclusive[selectedExclusive.length - 1];
            return {
                values: [activeExclusive],
                disabled: new Set(getFieldChoices(fieldKey).filter(function (choice) { return !exclusiveValues.includes(choice); })),
            };
        }
        return { values: selectedOther, disabled: new Set() };
    };
    useEffect(function () {
        questionnaireTimer.startSection("Searching for Work");
        // Antecedents prefetches this section already; only fetch if it's
        // not already cached in the store (e.g. direct navigation to this page).
        if (!knowledeData || knowledeData.length === 0) {
            dispatch(KnowledgeAction());
        }
    }, [dispatch]);
    var formattedResponses = useMemo(function () {
        if (!knowledeFormData)
            return knowledeFormData;
        var formatted = __assign({}, knowledeFormData);
        if (Array.isArray(formatted["jobseeker_jobsearchsource"])) {
            formatted["jobseeker_jobsearchsource"] = formatted["jobseeker_jobsearchsource"].join(",");
        }
        return formatted;
    }, [knowledeFormData]);
    useEffect(function () {
        if (!formattedResponses)
            return;
        var nextDisabled = {};
        Object.keys(EXCLUSIVE_CHECKBOX_FIELDS).forEach(function (fieldKey) {
            var rawValue = formattedResponses[fieldKey];
            var existing = Array.isArray(rawValue)
                ? rawValue
                : typeof rawValue === "string"
                    ? rawValue
                        .split(",")
                        .map(function (item) { return item.trim(); })
                        .filter(Boolean)
                    : [];
            var disabled = normalizeExclusiveCheckboxSelection(fieldKey, existing).disabled;
            if (disabled.size > 0) {
                nextDisabled[fieldKey] = disabled;
            }
        });
        setDisabledCheckboxOptions(nextDisabled);
    }, [formattedResponses, knowledeData]);
    var handleFormChange = function (newFormData) {
        var updatedFormData = __assign({}, newFormData);
        var nextDisabled = __assign({}, disabledCheckboxOptions);
        Object.keys(EXCLUSIVE_CHECKBOX_FIELDS).forEach(function (fieldKey) {
            var normalized = normalizeExclusiveCheckboxSelection(fieldKey, newFormData[fieldKey] || []);
            updatedFormData[fieldKey] = normalized.values;
            if (normalized.disabled.size > 0) {
                nextDisabled[fieldKey] = normalized.disabled;
            }
            else {
                delete nextDisabled[fieldKey];
            }
        });
        setDisabledCheckboxOptions(nextDisabled);
        return updatedFormData;
    };
    var handleFormSubmit = function (formData) {
        questionnaireTimer.endSection("Searching for Work");
        var sanitizedData = Object.keys(formData).reduce(function (acc, key) {
            acc[key] = DOMPurify.sanitize(formData[key]);
            return acc;
        }, {});
        // Convert comma-separated string to array
        if (typeof sanitizedData["jobseeker_jobsearchsource"] === "string") {
            sanitizedData["jobseeker_jobsearchsource"] = sanitizedData["jobseeker_jobsearchsource"]
                .split(",")
                .map(function (item) { return item.trim(); })
                .filter(Boolean);
        }
        else if (!sanitizedData["jobseeker_jobsearchsource"]) {
            sanitizedData["jobseeker_jobsearchsource"] = [];
        }
        setIsSubmitting(true);
        dispatch(KnowledgeFormAction(sanitizedData, loginData));
    };
    var handleBackBtn = function () {
        navigate("/questionnaire/antecedents");
    };
    useEffect(function () {
        if (success_knowlede_form && isSubmitting) {
            navigate("/questionnaire/generalSkills");
        }
    }, [success_knowlede_form, isSubmitting, navigate]);
    return (_jsxs("div", __assign({ className: "" }, { children: [error && _jsx("div", __assign({ className: "error-message" }, { children: error })), _jsx(QuestionnaireOrg, { title: "Searching for Work", content: "The next few questions will ask about your current job search activity.", data: knowledeData, responses: formattedResponses, loading: !knowledeData || knowledeData.length === 0, handleSubmit: handleFormSubmit, handleBackBtn: handleBackBtn, onChange: handleFormChange, classname: "knowledge-w", width: getQuestionnaireProgressWidth("knowledge", getQuestionnaireAttemptVariant(quesAttemptDetails)), disabledCheckboxOptions: disabledCheckboxOptions })] })));
}
export default KnowledgeQuestionnaire;
